import { memo, useContext } from 'react';
import DoneIcon from '@mui/icons-material/Done';
import { EyeOff } from 'react-feather';
import { type Cell } from 'react-table';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { WorkspaceObjectType } from '@/enums';
import type { IWorkspaceFileItem, WorkspaceObjectItem } from '@/types/workspace.table';
import styles from './style/Files.Table.Cells.module.css';

export function PublishedStatusCell(props: Cell<WorkspaceObjectItem>) {
  switch (props.row.original.object.typeId) {
    case WorkspaceObjectType.File:
      return <FilePublishedStatusCell {...(props as Cell<IWorkspaceFileItem>)} />;

    case WorkspaceObjectType.Folder:
    case WorkspaceObjectType.ProjectParent:
    case WorkspaceObjectType.Brand:
    case WorkspaceObjectType.CallTranscript:
    case WorkspaceObjectType.Workspace:
      return null;

    default:
      throw new UnreachableCaseError(props.row.original.object.typeId);
  }
}

const FilePublishedStatusCell = memo(({ row: { original: data } }: Cell<IWorkspaceFileItem>) => {
  const isPublished = data.file.isPublished;
  const tooltipPublished = 'Published';
  const tooltipUnpublished = 'Not Published';

  const { palette, theme: wl } = useContext(AppThemingContext);
  const publishedColor = wl?.enabled ? palette.primary.dark : '#2596be';
  const unpublishedColor = wl?.enabled ? palette.primary.light : '#737373';

  if (isPublished) {
    return (
      <Tooltip title={tooltipPublished}>
        <DoneIcon
          style={{
            fontSize: 19,
            color: publishedColor,
          }} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipUnpublished}>
      <EyeOff
        className={styles.unpublishedIcon}
        size={16}
        color={unpublishedColor} />
    </Tooltip>
  );
});

// Error class for unreachable cases
class UnreachableCaseError extends Error {
  constructor(val: never) {
    super(`Unreachable case: ${val}`);
  }
}
